"use server";

import { redirect } from "next/navigation";
import { revalidatePath } from "next/cache";
import { createClient } from "@/lib/supabase/server";
import {
  getAccessibleClientIds,
  requireAdmin,
  requireSession,
  requireStaff,
} from "@/lib/auth/session";
import { normalizeUrl, normalizeName } from "@/lib/utils/normalize";
import { coerceSEOCrawlConfig } from "@/lib/seo-crawl-config";
import { coerceAccessibilityConfig } from "@/lib/accessibility-config";
import { coercePageSpeedAuditConfig } from "@/lib/pagespeed/config";
import { coerceSitemapSource } from "@/lib/audits/sitemap-reuse";
import { indexCoverageSchedule, storedIndexCoverageConfig } from "@/lib/gsc-index/settings";
import type { MaxPages, StrategyConfig } from "@/lib/pagespeed/types";
import {
  parseTemplateConfig,
  parseProspectAuditDepth,
  buildProspectClientPreset,
  buildHostingClientPreset,
} from "@/lib/prospect-setup";
import { DEFAULT_AI_MODEL } from "@/lib/types/database";

const checkboxOn = (formData: FormData, name: string) => formData.getAll(name).includes("on");

async function findDuplicateClient(
  name: string,
  websiteUrl: string,
  excludeId?: string,
): Promise<{ id: string; name: string; field: "name" | "website" } | null> {
  const supabase = await createClient();
  const inputName = normalizeName(name);

  // Check name match server-side (case-insensitive)
  const { data: nameMatches } = await supabase
    .from("clients")
    .select("id, name")
    .ilike("name", inputName)
    .limit(10);

  if (nameMatches) {
    for (const m of nameMatches) {
      if (m.id !== excludeId && normalizeName(m.name) === inputName) {
        return { id: m.id, name: m.name, field: "name" };
      }
    }
  }

  // Check website match if provided
  if (websiteUrl) {
    const inputUrl = normalizeUrl(websiteUrl);
    if (inputUrl) {
      // Narrow to rows that mention this domain instead of scanning every
      // client with a website. normalizeUrl strips protocol/www/trailing
      // slash, so any genuine duplicate's stored URL contains the domain as a
      // substring; the exact normalized comparison below still gates a match,
      // so the ilike only ever over-includes (never misses).
      const domain = inputUrl.split("/")[0];
      const { data: urlCandidates } = await supabase
        .from("clients")
        .select("id, name, website_url")
        .ilike("website_url", `%${domain}%`)
        .limit(100);

      if (urlCandidates) {
        for (const client of urlCandidates) {
          if (client.id === excludeId) continue;
          if (normalizeUrl(client.website_url ?? "") === inputUrl) {
            return { id: client.id, name: client.name, field: "website" };
          }
        }
      }
    }
  }

  return null;
}

export async function createClientAction(formData: FormData) {
  await requireStaff();
  const supabase = await createClient();

  const name = formData.get("name") as string;
  const website_url = formData.get("website_url") as string;

  const duplicate = await findDuplicateClient(name, website_url);
  if (duplicate) {
    redirect(
      `/clients/new?error=duplicate&field=${duplicate.field}&existingId=${duplicate.id}&existingName=${encodeURIComponent(duplicate.name)}`,
    );
  }

  const services = formData.getAll("services").map(String).filter(Boolean);
  const status = (formData.get("status") as string) || "prospect";

  const { data, error } = await supabase.from("clients").insert({
    name,
    website_url,
    industry: (formData.get("industry") as string) || null,
    status,
    services,
  }).select("id").single();

  if (error) throw new Error(error.message);

  // Publishing landing preference. Applied as a separate fail-soft update, the
  // same pattern the tool-settings save uses: the column arrives with the
  // 20260726_publishing_content_items migration, and company creation must
  // succeed before it is applied.
  if (formData.get("publishing_content_client_landing") === "on") {
    const { error: landingErr } = await supabase
      .from("clients")
      .update({ publishing_content_client_landing: true })
      .eq("id", data.id);
    if (landingErr) {
      console.warn(
        "[createClient] publishing_content_client_landing not saved (migration pending?):",
        landingErr.message,
      );
    }
  }

  // Company project manager. Same separate fail-soft pattern: the column
  // arrives with the 20260910_forms migration, and company creation must
  // succeed before it is applied.
  const projectManagerId = (formData.get("project_manager_id") as string) || null;
  if (projectManagerId) {
    const { error: pmErr } = await supabase
      .from("clients")
      .update({ project_manager_id: projectManagerId })
      .eq("id", data.id);
    if (pmErr) {
      console.warn(
        "[createClient] project_manager_id not saved (migration pending?):",
        pmErr.message,
      );
    }
  }

  const template = parseTemplateConfig(formData.get("template_config") as string | null);

  if (template === "prospect") {
    const depth = parseProspectAuditDepth(formData.get("prospect_audit_depth") as string | null);

    // Enable the audits at the chosen depth with manual routines + uptime on.
    const { error: presetErr } = await supabase
      .from("clients")
      .update(buildProspectClientPreset(depth))
      .eq("id", data.id);
    if (presetErr) {
      console.error("[createClient] Failed to apply prospect preset:", presetErr.message);
    }

    // Enable SEO Research (DataForSEO) via its integration row — best effort:
    // never block client creation if this fails (e.g. missing creds). Uses the
    // admin client: integration writes are service-role-only since the
    // 202609050001_security_boundaries migration.
    const { createAdminClient } = await import("@/lib/supabase/admin");
    const { error: dfsErr } = await createAdminClient()
      .from("integrations")
      .insert({ client_id: data.id, provider: "dataforseo", credentials: {}, status: "connected" });
    if (dfsErr) {
      console.error("[createClient] Failed to enable SEO Research:", dfsErr.message);
    }

    redirect(`/clients/${data.id}/setup?prospect_init=${depth}`);
  }

  if (template === "hosting") {
    // Hosting: the five tools on manual routines (uptime on) with app-default
    // audit configs. No SEO Research, no one-time auto-run.
    const { error: presetErr } = await supabase
      .from("clients")
      .update(buildHostingClientPreset())
      .eq("id", data.id);
    if (presetErr) {
      console.error("[createClient] Failed to apply hosting preset:", presetErr.message);
    }
  }

  redirect(`/clients/${data.id}/setup`);
}

export async function updateClientAction(
  _prevState: { error?: string; field?: string; existingId?: string; existingName?: string } | null,
  formData: FormData,
) {
  await requireStaff();
  const supabase = await createClient();
  const id = formData.get("id") as string;
  const name = formData.get("name") as string;
  const website_url = formData.get("website_url") as string;

  const duplicate = await findDuplicateClient(name, website_url, id);
  if (duplicate) {
    return {
      error: "duplicate",
      field: duplicate.field,
      existingId: duplicate.id,
      existingName: duplicate.name,
    };
  }

  const services = formData.getAll("services").map(String).filter(Boolean);

  const { error } = await supabase
    .from("clients")
    .update({
      name,
      website_url,
      industry: (formData.get("industry") as string) || null,
      status: formData.get("status") as string,
      services,
      slack_channel_id: (formData.get("slack_channel_id") as string) || null,
    })
    .eq("id", id);

  if (error) return { error: error.message };

  // Company project manager. Applied as a separate fail-soft update so a
  // pending 20260910_forms migration cannot break the details save, and only
  // when the select was actually rendered (the field is absent otherwise, and
  // an absent field must never clear an existing assignment).
  if (formData.has("project_manager_id")) {
    const { error: pmErr } = await supabase
      .from("clients")
      .update({ project_manager_id: (formData.get("project_manager_id") as string) || null })
      .eq("id", id);
    if (pmErr) {
      console.warn(
        "[updateClient] project_manager_id not saved (migration pending?):",
        pmErr.message,
      );
    }
  }

  redirect(`/clients/${id}/setup?saved=details`);
}

export async function saveToolSettingsAction(formData: FormData): Promise<{ success: boolean; error?: string }> {
  await requireStaff();
  const supabase = await createClient();
  const id = formData.get("id") as string;

  try {
    const { createAdminClient } = await import("@/lib/supabase/admin");
    const { readCache } = await import("@/lib/uptime/storage");
    const { DEFAULT_MONITOR_CONFIG } = await import("@/lib/uptime/model");
    const { saveMonitorSettings } = await import("@/lib/uptime/settings");
    const db = createAdminClient();
    const config = (await readCache<import("@/lib/uptime/model").MonitorConfig>(db, id, "uptime_config"))?.data ?? DEFAULT_MONITOR_CONFIG;
    await saveMonitorSettings(db, id, {
      ...config,
      paused: formData.get("uptime_paused") === "on" || formData.get("uptime_monitoring") !== "on",
      policy: formData.get("uptime_up_policy") === "on" ? "reachable" : "strict",
      cms: ((formData.get("cms_error_monitoring") as string) || null) as "wordpress" | "joomla" | null,
    });
  } catch (error) { return { success: false, error: error instanceof Error ? error.message : "Could not save monitoring settings" }; }

  const { error } = await supabase
    .from("clients")
    .update({
      uptime_monitoring: formData.get("uptime_monitoring") === "on",
      scheduled_reports_enabled: formData.get("scheduled_reports_enabled") === "on",
      audit_schedule_site_audit: (formData.get("audit_schedule_site_audit") as string) || "none",
      audit_schedule_seo_crawl: (formData.get("audit_schedule_seo_crawl") as string) || "none",
      audit_schedule_accessibility: (formData.get("audit_schedule_accessibility") as string) || "none",
      audit_schedule_pagespeed: (formData.get("audit_schedule_pagespeed") as string) || "none",
      audit_schedule_ai_search_health: (formData.get("audit_schedule_ai_search_health") as string) || "none",
      pagespeed_config: coercePageSpeedAuditConfig({
        repetitions: Number(formData.get("pagespeed_repetitions")) === 3 ? 3 : 1,
        lighthouseTimeout: Number(formData.get("pagespeed_lighthouse_timeout")) || undefined,
        sitemapSource: coerceSitemapSource(formData.get("pagespeed_sitemap_source")),
        customUrls: (() => {
          try {
            const value = JSON.parse(String(formData.get("pagespeed_custom_urls") ?? "[]"));
            return Array.isArray(value) ? value : [];
          } catch { return []; }
        })(),
        customUrlMode: formData.get("pagespeed_custom_mode") === "mixed" ? "mixed" : "only",
        strategy: (formData.get("pagespeed_strategy") as StrategyConfig | null) ?? undefined,
        maxPages: (Number(formData.get("pagespeed_max_pages")) || undefined) as MaxPages | undefined,
        timeout: Number(formData.get("pagespeed_timeout")) || undefined,
        respectRobotsTxt: checkboxOn(formData, "pagespeed_respectRobotsTxt"),
        includeNonSitemapPages: checkboxOn(formData, "pagespeed_includeNonSitemapPages"),
      }),
      seo_crawl_config: coerceSEOCrawlConfig({
        renderedPages: Number(formData.get("seo_crawl_renderedPages")),
        maxLinks: Number(formData.get("seo_crawl_maxLinks")) || undefined,
        expectedSearchBots: formData.getAll("seo_crawl_expectedSearchBots").map(String),
        maxPages: Number(formData.get("seo_crawl_maxPages")) || undefined,
        maxDepth: Number(formData.get("seo_crawl_maxDepth")) || undefined,
        concurrency: Number(formData.get("seo_crawl_concurrency")) || undefined,
        timeout: Number(formData.get("seo_crawl_timeout")) || undefined,
        respectRobotsTxt: checkboxOn(formData, "seo_crawl_respectRobotsTxt"),
        includeNonSitemapPages: checkboxOn(formData, "seo_crawl_includeNonSitemapPages"),
      }),
      accessibility_config: coerceAccessibilityConfig({
        maxPages: Number(formData.get("accessibility_maxPages")) || undefined,
        concurrency: Number(formData.get("accessibility_concurrency")) || undefined,
        timeout: Number(formData.get("accessibility_timeout")) || undefined,
        comparisonScanLimit: Number(formData.get("accessibility_comparisonScanLimit")) || undefined,
        respectRobotsTxt: checkboxOn(formData, "accessibility_respectRobotsTxt"),
        includeNonSitemapPages: checkboxOn(formData, "accessibility_includeNonSitemapPages"),
      }),
      keyword_tracker_enabled: formData.get("keyword_tracker_enabled") === "on",
      ai_recommendations_enabled: formData.get("ai_recommendations_enabled") === "on",
      // Dependent toggle: Deep Research cannot be on without AI Recommendations
      // (the form disables it, and this re-enforces it server-side).
      deep_research_enabled:
        formData.get("deep_research_enabled") === "on" &&
        formData.get("ai_recommendations_enabled") === "on",
      // All AI generation is locked to the default model (Sonnet); the form no
      // longer offers a choice, and saving normalizes any older stored value.
      ai_recommendations_model: DEFAULT_AI_MODEL,
    })
    .eq("id", id);

  if (error) return { success: false, error: error.message };

  // Scheduled AI Recommendations refresh (Mon/Thu batch). Fail-soft: the
  // column arrives with the 20260905 migration. Dependent on AI
  // Recommendations like Deep Research; the form disables it, and this
  // re-enforces it server-side.
  const { error: autoRefreshError } = await supabase
    .from("clients")
    .update({
      ai_recommendations_auto_refresh:
        formData.get("ai_recommendations_auto_refresh") === "on" &&
        formData.get("ai_recommendations_enabled") === "on",
    })
    .eq("id", id);
  if (autoRefreshError) {
    console.warn("[tool settings] ai_recommendations_auto_refresh not saved (migration pending?):", autoRefreshError.message);
  }

  // Index Coverage on/off and weekly vs manual. Same fail-soft pattern: the
  // column arrives with the 20261016_index_coverage_setting migration, and
  // until then every company reads as weekly.
  const { error: indexCoverageError } = await supabase
    .from("clients")
    .update({ audit_schedule_gsc_index: indexCoverageSchedule(formData.get("audit_schedule_gsc_index")) })
    .eq("id", id);
  if (indexCoverageError) {
    console.warn("[tool settings] audit_schedule_gsc_index not saved (migration pending?):", indexCoverageError.message);
  }
  // What the monthly run checks. Its own fail-soft update: the column arrives
  // with the 20261016_index_coverage_setting_monthly migration.
  if (formData.has("gsc_index_scope")) {
    const { error: indexConfigError } = await supabase
      .from("clients")
      .update({
        gsc_index_config: storedIndexCoverageConfig({
          scope: formData.get("gsc_index_scope"),
          maxPages: Number(formData.get("gsc_index_max_pages")) || undefined,
        }),
      })
      .eq("id", id);
    if (indexConfigError) {
      console.warn("[tool settings] gsc_index_config not saved (migration pending?):", indexConfigError.message);
    }
  }

  // Social Publishing toggle. Separate fail-soft update: the column arrives
  // with the 20260712 migration and the rest of the save must work before it.
  const { error: socialError } = await supabase
    .from("clients")
    .update({ social_publishing_enabled: formData.get("social_publishing_enabled") === "on" })
    .eq("id", id);
  if (socialError) {
    console.warn("[tool settings] social_publishing_enabled not saved (migration pending?):", socialError.message);
  }

  // SEO Research (DataForSEO) is enabled by the existence of its integration
  // row. Sync that row to the Setup toggle: create on enable, remove on
  // disable. This runs BEFORE the Competitor Profiles / AEO Research dependency
  // checks so all three can be enabled in one save, and on the admin client
  // because integration writes are service-role-only since the
  // 202609050001_security_boundaries migration.
  const dataforseoEnabled = formData.get("dataforseo_enabled") === "on";
  const { createAdminClient: createAdmin } = await import("@/lib/supabase/admin");
  const admin = createAdmin();
  const { data: existingDataForSEO, error: selErr } = await admin
    .from("integrations")
    .select("id")
    .eq("client_id", id)
    .eq("provider", "dataforseo")
    .maybeSingle();
  if (selErr) return { success: false, error: selErr.message };

  if (dataforseoEnabled && !existingDataForSEO) {
    const { error: insErr } = await admin
      .from("integrations")
      .insert({ client_id: id, provider: "dataforseo", credentials: {}, status: "connected" });
    if (insErr) return { success: false, error: insErr.message };
  } else if (!dataforseoEnabled && existingDataForSEO) {
    const { error: delErr } = await admin
      .from("integrations")
      .delete()
      .eq("id", existingDataForSEO.id);
    if (delErr) return { success: false, error: delErr.message };
  }

  // Competitor Profiles toggle. Fail-soft (column arrives with the
  // 20260730_competitor_profiles migration) and dependent on a connected
  // DataForSEO integration (synced above); the form disables it without one,
  // and this re-enforces it server-side like the Deep Research dependency.
  const competitorProfilesOn =
    formData.get("competitor_profiles_enabled") === "on" && dataforseoEnabled;
  const { error: profilesError } = await supabase
    .from("clients")
    .update({ competitor_profiles_enabled: competitorProfilesOn })
    .eq("id", id);
  if (profilesError) {
    console.warn("[tool settings] competitor_profiles_enabled not saved (migration pending?):", profilesError.message);
  }

  // AEO Research toggle. Same fail-soft pattern (column arrives with the
  // 20260731_aeo_research migration) and the same server-side DataForSEO
  // dependency as Competitor Profiles.
  const aeoResearchOn =
    formData.get("aeo_research_enabled") === "on" && dataforseoEnabled;
  const { error: aeoError } = await supabase
    .from("clients")
    .update({ aeo_research_enabled: aeoResearchOn })
    .eq("id", id);
  if (aeoError) {
    console.warn("[tool settings] aeo_research_enabled not saved (migration pending?):", aeoError.message);
  }

  // Sitemap Planner toggle. Same fail-soft pattern: the column arrives with
  // the 20260711_sitemap_planner migration.
  const { error: sitemapError } = await supabase
    .from("clients")
    .update({ sitemap_planner_enabled: formData.get("sitemap_planner_enabled") === "on" })
    .eq("id", id);
  if (sitemapError) {
    console.warn("[tool settings] sitemap_planner_enabled not saved (migration pending?):", sitemapError.message);
  }

  // Site Feedback client visibility. Same fail-soft pattern: the column arrives
  // with the 20261002_site_feedback migration.
  const { error: siteFeedbackError } = await supabase
    .from("clients")
    .update({ site_feedback_enabled: formData.get("site_feedback_enabled") === "on" })
    .eq("id", id);
  if (siteFeedbackError) {
    console.warn("[tool settings] site_feedback_enabled not saved (migration pending?):", siteFeedbackError.message);
  }

  // Content-list landing for client users. Same fail-soft pattern: the column
  // arrives with the 20260726_publishing_content_items migration.
  const { error: landingError } = await supabase
    .from("clients")
    .update({
      publishing_content_client_landing:
        formData.get("publishing_content_client_landing") === "on",
    })
    .eq("id", id);
  if (landingError) {
    console.warn(
      "[tool settings] publishing_content_client_landing not saved (migration pending?):",
      landingError.message,
    );
  }

  // Sync uptime_monitoring to linked website
  const uptimeValue = formData.get("uptime_monitoring") === "on";
  const { syncClientToWebsite } = await import("@/lib/actions/websites");
  await syncClientToWebsite(id, "uptime_monitoring", uptimeValue);

  // Refresh the client layout so newly enabled tools surface in the sidebar nav
  // and dashboard widgets immediately, without waiting for the first audit run.
  revalidatePath(`/clients/${id}`, "layout");

  return { success: true };
}

export async function setUptimePausedAction(
  clientId: string,
  paused: boolean
): Promise<{ success: boolean; error?: string }> {
  await requireStaff();
  try {
    const { createAdminClient } = await import("@/lib/supabase/admin");
    const { readCache } = await import("@/lib/uptime/storage");
    const { saveMonitorSettings } = await import("@/lib/uptime/settings");
    const { DEFAULT_MONITOR_CONFIG } = await import("@/lib/uptime/model");
    const { loadMonitoredClient } = await import("@/lib/uptime/runner");
    const sb = createAdminClient();
    const client = await loadMonitoredClient(sb, clientId);
    const config = (await readCache<import("@/lib/uptime/model").MonitorConfig>(sb, clientId, "uptime_config"))?.data ?? DEFAULT_MONITOR_CONFIG;
    await saveMonitorSettings(sb, clientId, { ...config, paused, policy: client.uptime_up_policy, cms: client.cms_error_monitoring });
    return { success: true };
  } catch (error) { return { success: false, error: error instanceof Error ? error.message : "Could not change monitoring" }; }

}

export async function deleteClientAction(formData: FormData) {
  await requireAdmin();
  const supabase = await createClient();
  const id = formData.get("id") as string;

  // Site Feedback screenshots, replays and attachments, and Media Library
  // files, live in buckets the cascade cannot reach, and the rows are the only
  // list of them: read the paths first (a failed read stops the delete),
  // remove the files after.
  const { createAdminClient } = await import("@/lib/supabase/admin");
  const { companyFilePaths } = await import("@/lib/site-feedback/company-files");
  const { removeObjects } = await import("@/lib/site-feedback/storage");
  const { companyMediaPaths } = await import("@/lib/media/company-files");
  const { removeMediaObjects } = await import("@/lib/media/assets");
  const admin = createAdminClient();
  const siteFeedbackFiles = await companyFilePaths(admin, id);
  const mediaFiles = await companyMediaPaths(admin, id);

  const { data: deleted, error } = await supabase.from("clients").delete().eq("id", id).select("id");

  if (error) throw new Error(error.message);

  // Only when the company row really went: RLS can turn a delete into a
  // silent no-op, and the surviving rows would still point at these files.
  if ((deleted ?? []).length > 0) {
    await removeObjects(admin, siteFeedbackFiles);
    await removeMediaObjects(admin, mediaFiles);
  }

  redirect("/");
}

export async function fetchClientListAction(): Promise<
  { id: string; name: string; status: string }[]
> {
  const session = await requireSession();
  const supabase = await createClient();
  const accessibleClientIds = await getAccessibleClientIds(session);
  if (accessibleClientIds === null) {
    const { data } = await supabase
      .from("clients")
      .select("id, name, status")
      .order("status")
      .order("name");
    return (data ?? []) as { id: string; name: string; status: string }[];
  }

  if (accessibleClientIds.length === 0) return [];
  const { data } = await supabase
    .from("clients")
    .select("id, name, status")
    .in("id", accessibleClientIds)
    .order("status")
    .order("name");

  return (data ?? []) as { id: string; name: string; status: string }[];
}
